import type { LessonPhase, LessonResourceRecommendation, ActivityStep } from '../types/lessonPlan'

const createId = () => Math.random().toString(36).slice(2, 10)

const cleanInlineMarkdown = (value: string) =>
  value
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .trim()

const inferMode = (text: string): ActivityStep['mode'] => {
  const lower = text.toLowerCase()
  if (lower.includes('pair') || lower.includes('partner')) return 'pair'
  if (lower.includes('group') || lower.includes('collaborat')) return 'group'
  if (lower.includes('class') || lower.includes('whole class')) return 'class'
  return 'individual'
}

const mapHeadingToType = (heading: string): LessonPhase['type'] => {
  const lower = heading.toLowerCase()
  if (lower.includes('introduction') || lower.includes('engage') || lower.includes('hook')) return 'engage'
  if (lower.includes('direct instruction') || lower.includes('mini lesson') || lower.includes('explain')) return 'inquire'
  if (lower.includes('guided practice')) return 'apply'
  if (lower.includes('independent practice') || lower.includes('application')) return 'apply'
  if (lower.includes('closure') || lower.includes('assessment') || lower.includes('exit ticket')) return 'assess'
  return 'inquire'
}

const inferResourceType = (text: string): LessonResourceRecommendation['type'] => {
  const lower = text.toLowerCase()
  if (lower.includes('video')) return 'video'
  if (lower.includes('worksheet') || lower.includes('doc') || lower.includes('slides') || lower.includes('handout')) return 'doc'
  if (lower.includes('simulator') || lower.includes('tool')) return 'tool'
  return 'link'
}

const buildRecommendations = (subject: string, phaseType: LessonPhase['type'], topic: string): LessonResourceRecommendation[] => {
  const scienceLike = ['science', 'physics', 'chemistry', 'biology']
  const subjectLower = subject.toLowerCase()
  const recs: LessonResourceRecommendation[] = []

  if (scienceLike.some((item) => subjectLower.includes(item))) {
    recs.push({
      id: createId(),
      title: `PhET interactive simulation for ${topic}`,
      url: 'https://phet.colorado.edu/',
      type: 'tool',
      source: 'rule',
      description: 'Use a simulation to visualize the core concept and support inquiry-based exploration.',
    })
  }

  if (phaseType === 'engage') {
    recs.push({
      id: createId(),
      title: `Short hook video about ${topic}`,
      url: 'https://www.youtube.com/',
      type: 'video',
      source: 'rule',
      description: 'A short opening clip can activate curiosity and frame the lesson context.',
    })
  }

  if (phaseType === 'apply') {
    recs.push({
      id: createId(),
      title: `${topic} practice worksheet`,
      url: 'https://www.khanacademy.org/',
      type: 'doc',
      source: 'rule',
      description: 'Use structured practice or extension tasks for transfer and reinforcement.',
    })
  }

  if (phaseType === 'assess') {
    recs.push({
      id: createId(),
      title: 'Exit ticket / quick formative check',
      url: 'https://forms.google.com/',
      type: 'link',
      source: 'rule',
      description: 'A fast end-of-lesson check to capture understanding and misconceptions.',
    })
  }

  recs.push({
    id: createId(),
    title: `${topic} reference article`,
    url: 'https://www.britannica.com/',
    type: 'link',
    source: 'rule',
    description: 'Useful for background reading, teacher preparation, or enrichment support.',
  })

  return recs.slice(0, 4)
}

export const parseLessonPlanToPhases = (markdown: string, subject = '', topic = ''): LessonPhase[] => {
  const lines = markdown.split('\n')
  const phases: LessonPhase[] = []
  let currentHeading = ''
  let currentDuration = 10
  let currentBullets: string[] = []
  let collecting = false
  let objective = ''
  let keyQuestions = ''
  let misconceptions = ''
  let notes = ''
  let materials: string[] = []
  let currentSection = ''

  const flush = () => {
    if (!collecting || !currentHeading) return

    const type = mapHeadingToType(currentHeading)
    const cleanedBullets = currentBullets.map(cleanInlineMarkdown).filter(Boolean)

    const script = cleanedBullets
      .filter((bullet) => !/(worksheet|slides|video|handout|rubric|simulator|tool|link)/i.test(bullet))
      .map((bullet) => ({
        id: createId(),
        role: /students?/i.test(bullet) ? 'student' as const : 'teacher' as const,
        content: bullet,
      }))

    const activities = cleanedBullets
      .filter((bullet) => /(students?|work|complete|discuss|practice|observe|record|pair|group|class)/i.test(bullet))
      .map((bullet) => ({
        id: createId(),
        action: bullet,
        duration: Math.max(3, Math.round(currentDuration / Math.max(cleanedBullets.length, 1))),
        mode: inferMode(bullet),
        completed: false,
      }))

    const resources = [
      ...materials.map((item) => ({
        id: createId(),
        title: item,
        url: '',
        type: inferResourceType(item),
      })),
      ...cleanedBullets
        .filter((bullet) => /(worksheet|slides|video|handout|rubric|simulator|tool|link)/i.test(bullet))
        .map((bullet) => ({
          id: createId(),
          title: bullet,
          url: '',
          type: inferResourceType(bullet),
        })),
    ].slice(0, 6)

    phases.push({
      id: createId(),
      title: currentHeading,
      type,
      duration: currentDuration,
      status: 'draft',
      isActive: false,
      script: script.length ? script : [{ id: createId(), role: 'teacher', content: cleanedBullets[0] || currentHeading }],
      activities,
      resources,
      designNote: {
        objective,
        keyQuestions,
        misconceptions,
        notes,
      },
      recommendations: buildRecommendations(subject, type, topic || currentHeading),
      settings: {
        interactionMode: activities[0]?.mode || 'class',
        formativeAssessment: type === 'assess',
        standards: [],
      },
    })

    currentHeading = ''
    currentDuration = 10
    currentBullets = []
    collecting = false
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    const headingMatch = line.match(/^###\s+(.+?)(?:\s*\((\d+)\s*minutes\))?$/i)
    const sectionMatch = line.match(/^##\s+(.+)$/)

    if (sectionMatch) {
      currentSection = sectionMatch[1].toLowerCase()
      if (collecting) {
        flush()
      }
      continue
    }

    if (headingMatch) {
      flush()
      currentHeading = headingMatch[1].trim()
      currentDuration = headingMatch[2] ? Number(headingMatch[2]) : 10
      collecting = true
      continue
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)/)
    const orderedMatch = line.match(/^\d+\.\s+(.+)/)
    const extracted = bulletMatch?.[1] || orderedMatch?.[1] || ''

    if (currentSection.includes('learning objectives') && extracted) {
      objective += `${objective ? '\n' : ''}${cleanInlineMarkdown(extracted)}`
    } else if (currentSection.includes('materials') && extracted) {
      materials.push(cleanInlineMarkdown(extracted))
    } else if ((currentSection.includes('assessment') || currentSection.includes('reflection')) && extracted) {
      keyQuestions += `${keyQuestions ? '\n' : ''}${cleanInlineMarkdown(extracted)}`
    } else if (currentSection.includes('differentiation') && extracted) {
      notes += `${notes ? '\n' : ''}${cleanInlineMarkdown(extracted)}`
    } else if (collecting && extracted) {
      currentBullets.push(extracted)
    } else if (/misconception/i.test(line)) {
      misconceptions += `${misconceptions ? '\n' : ''}${cleanInlineMarkdown(line)}`
    }
  }

  flush()

  if (!phases.length) {
    phases.push({
      id: createId(),
      title: 'Generated Lesson Phase',
      type: 'inquire',
      duration: 15,
      status: 'draft',
      isActive: true,
      script: [{ id: createId(), role: 'teacher', content: markdown.slice(0, 200) || 'Generated lesson content' }],
      activities: [],
      resources: materials.map((item) => ({ id: createId(), title: item, url: '', type: inferResourceType(item) })),
      designNote: { objective, keyQuestions, misconceptions, notes },
      recommendations: buildRecommendations(subject, 'inquire', topic || 'lesson'),
      settings: { interactionMode: 'class', formativeAssessment: false, standards: [] },
    })
  }

  return phases.map((phase, index) => ({ ...phase, isActive: index === 0 }))
}
